import { Button, notification, Popconfirm } from "antd"
import { Minus, Plus } from "lucide-react"
import { memo, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { removeChampion } from "~/api/champions-tracking"
import { addToYourPeopleList } from "~/api/people"
import { OSLoader } from "~/components/base-loader"
import CustomTooltip from "~/components/custom-tooltip"
import useAddedChampionsStore from "~/store/added-champions"
import useGlobalStore from "~/store/global"
import usePeopleListStore from "~/store/people-list"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { toCapitalize } from "~/utils/helpers"
import { getLocationDisplay } from "~/utils/location-helpers"

import { EVENTS } from "../champions-tracking/constants"

export const AddToPeopleListButton = memo(
  ({ person, source }: { person: any; source?: string }) => {
    const { t } = useTranslation()
    const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
    const [notificationApi, contextHolder] = notification.useNotification()
    const tokenInfo = useGlobalStore((store) => store.tokenInfo)
    const refetchChampions = usePeopleListStore(
      (store) => store.refetchChampions
    )
    const [loading, setLoading] = useState(false)

    // Persist +/− state across remounts (e.g. navigating away and back) via a
    // linkedin_url-keyed store, since the event-people list API doesn't return
    // champion_id. The store value (if this person has been added/removed this
    // session) wins; otherwise fall back to any server-provided champion_id.
    const linkedinUrl = person?.linkedin_url ?? ""
    const markAdded = useAddedChampionsStore((store) => store.markAdded)
    const markRemoved = useAddedChampionsStore((store) => store.markRemoved)
    const storedChampionId = useAddedChampionsStore((store) =>
      linkedinUrl ? store.added[linkedinUrl] : undefined
    )
    const championId =
      storedChampionId !== undefined
        ? storedChampionId
        : (person?.champion_id ?? "")

    const handleAddToYourPeopleList = async () => {
      if (!tokenInfo) {
        notificationApi.error({
          message: t("people_list.label_authentication_required"),
          placement: "topRight"
        })
        return
      }

      if (!person?.linkedin_url) {
        notificationApi.error({
          message: t("people_list.label_linkedin_url_required"),
          placement: "topRight"
        })
        return
      }

      const inferredAccountId =
        person?.accId ??
        person?.account_id ??
        person?.organization_id ??
        person?.oppDetails?.[0]?.account_id ??
        null
      const normalizedAccountId =
        typeof inferredAccountId === "string"
          ? inferredAccountId.trim() !== "" && !isNaN(Number(inferredAccountId))
            ? Number(inferredAccountId)
            : null
          : (inferredAccountId ?? null)

      setLoading(true)
      const baseProps = getBaseEventProps({
        feature: "people_list",
        accountId: person?.account_id,
        accountName: person?.account_name
      })
      try {
        const payload = {
          linkedin_urls: [person?.linkedin_url],
          account_id: normalizedAccountId
        }

        track(EVENTS.ADD_PEOPLE_LIST_CLICKED, {
          ...baseProps,
          timestamp: Date.now(),
          status: "inprogress",
          champion_name: person?.name,
          champion_organization: person?.account_name,
          champion_role: person?.job_title
        })
        const response = await addToYourPeopleList(
          tokenInfo,
          payload,
          linkedTenantId
        )
        if (linkedinUrl) {
          markAdded(
            linkedinUrl,
            response?.data?.data?.processed[0]?.champion_id ?? ""
          )
        }
        notificationApi.success({
          message:
            source === "list"
              ? t("people_list.label_person_being_added")
              : t("people_list.label_person_added"),
          placement: "topRight"
        })
        track(EVENTS.ADD_PEOPLE_LIST_CLICKED, {
          ...baseProps,
          timestamp: Date.now(),
          status: "success",
          champion_name: person?.name,
          champion_organization: person?.account_name,
          champion_role: person?.job_title
        })
        if (refetchChampions) {
          refetchChampions()
        }
      } catch (error: any) {
        let errorMessage = t("people_list.label_failed_to_add_person")
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (error?.message) {
          errorMessage = error.message
        } else if (typeof error === "string") {
          errorMessage = error
        }
        if (
          errorMessage &&
          typeof errorMessage === "object" &&
          (errorMessage as any).message
        ) {
          errorMessage = (errorMessage as any).message
        }
        const normalizedError =
          typeof errorMessage === "string" ? errorMessage.toLowerCase() : ""
        if (
          normalizedError.includes("duplicate") ||
          normalizedError.includes("unique constraint") ||
          normalizedError.includes("already exists")
        ) {
          errorMessage = t("people_list.label_person_already_in_list")
        }
        notificationApi.error({
          message: errorMessage,
          placement: "topRight"
        })
        track(EVENTS.ADD_PEOPLE_LIST_CLICKED, {
          ...baseProps,
          timestamp: Date.now(),
          status: "failed",
          champion_name: person?.name,
          champion_organization: person?.account_name,
          champion_role: person?.job_title
        })
      } finally {
        setLoading(false)
      }
    }

    const handleRemoveChampion = useCallback(
      async (championId: string) => {
        if (!championId || !tokenInfo) return
        try {
          await removeChampion(tokenInfo, championId, linkedTenantId)
          notificationApi.success({
            message: t("people_list.label_person_removed_from_list"),
            placement: "topRight"
          })
          if (linkedinUrl) markRemoved(linkedinUrl)
        } catch {
          notificationApi.error({
            message: t("people_list.label_failed_to_remove_person"),
            placement: "topRight"
          })
        }
      },
      [notificationApi, tokenInfo, linkedTenantId, t, linkedinUrl, markRemoved]
    )

    const onHandleOpenDelete = () => {
      const baseProps = getBaseEventProps({
        feature: "people_list",
        accountId: person?.account_id,
        accountName: person?.account_name
      })
      track(EVENTS.REMOVE_PEOPLE_LIST_CLICKED, {
        ...baseProps,
        timestamp: Date.now(),
        champion_name: person?.name,
        champion_organization: person?.account_name,
        champion_role: person?.job_title
      })
      track(EVENTS.REMOVE_PEOPLE_LIST_CONFIRMATION_DISPLAYED, {
        ...baseProps,
        timestamp: Date.now(),
        champion_name: person?.name,
        champion_organization: person?.account_name,
        champion_role: person?.job_title
      })
    }
    const onDeleteCancelHandler = () => {
      track(EVENTS.REMOVE_PEOPLE_LIST_CANCELLED, {
        ...getBaseEventProps({
          feature: "people_list",
          accountId: person?.account_id,
          accountName: person?.account_name
        }),
        timestamp: Date.now(),
        champion_name: person?.name,
        champion_organization: person?.account_name,
        champion_role: person?.job_title
      })
    }

    return (
      <>
        {contextHolder}
        {!championId ? (
          <Button
            className="bg-transparent rounded-lg border-[0.5px] dark:border-primary-light border-primary p-2 h-6 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAddToYourPeopleList}
            disabled={loading || !person?.name}
            title={t("people_list.label_add_this_person_to_list")}
            data-testid="add-to-your-people-list"
          >
            {loading ? (
              <OSLoader className="xs" />
            ) : (
              <CustomTooltip
                title={
                  <p className="text-xs">
                    {t("people_list.label_add_to_people_list")}
                  </p>
                }
              >
                <Plus
                  size={14}
                  strokeWidth={1.5}
                  className="text-primary dark:text-primary-light w-4 h-4 min-w-4 min-h-4"
                />
              </CustomTooltip>
            )}
          </Button>
        ) : (
          <Popconfirm
            title={
              <span data-testid="remove-from-people-list-popconfirm">
                {t("people_list.label_remove")}
              </span>
            }
            description={
              <p className="text-xs">
                {t("people_list.label_remove_confirm", {
                  name: (() => {
                    const nameDisplay =
                      getLocationDisplay(person?.name) || person?.name
                    return nameDisplay
                      ? toCapitalize(nameDisplay.split(" ")[0])
                      : (nameDisplay ?? "")
                  })()
                })}
              </p>
            }
            okText={t("people_list.label_remove")}
            cancelText={t("label_cancel")}
            onConfirm={handleRemoveChampion.bind(null, championId)}
            onOpenChange={onHandleOpenDelete}
            onCancel={onDeleteCancelHandler}
            placement="bottomLeft"
            arrow={false}
            cancelButtonProps={{
              className: "text-xs",
              "data-testid": "remove-from-people-list-cancel-button"
            }}
            okButtonProps={{
              className: "text-xs",
              "data-testid": "remove-from-people-list-confirm-button"
            }}
          >
            <Button
              className="bg-transparent rounded-lg border-[0.5px] dark:border-primary-light p-2 h-6 !border-red-400"
              data-testid="remove-from-your-people-list"
            >
              <Minus size={12} strokeWidth={3} className="text-red-500" />
            </Button>
          </Popconfirm>
        )}
      </>
    )
  }
)
AddToPeopleListButton.displayName = "AddToPeopleListButton"
